import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CircleCheck from '@/assets/icons/operational-dashboard/circle-check.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Card } from '@/components/atoms/Card';
import InfoTip from '@/components/atoms/InfoTip';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type { HealthOverviewData } from '@/domain/health';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';

import { sortHealthOverviewParametersByNavigationOrder } from '../_utils/healthOverviewNavigationOrder';

import HealthAlertCard from './components/HealthAlertCard/HealthAlertCard';
import HealthDashboardInfoPanel from './components/HealthDashboardInfoPanel/HealthDashboardInfoPanel';
import HealthDashboardSkeleton from './components/HealthDashboardSkeleton/HealthDashboardSkeleton';
import LaksvelOverviewChart from './components/LaksvelOverviewChart/LaksvelOverviewChart';
import { compareHealthAlertParameters, isHealthAlertParameter } from './healthAlertDisplay';
import { getHealthMetricUiConfig } from './healthMetricConfig';
import { withScoresOnSelectedDay } from './healthOverviewChartMappers';

import styles from './HealthDashboard.module.scss';

interface HealthDashboardViewProps {
    /** Latest-day status. Its range stays the default, so a date change never rebuilds the alerts. */
    statusOverview: HealthOverviewData | undefined;
    /** Overview rows for the picked date range. */
    overview: HealthOverviewData | undefined;
    healthBaseUrl: string;
    /** The cage-scoped query string (`fg`, daypart, unit) the detail links carry, or `''`. */
    detailSearch: string;
    isInitialLoading: boolean;
    isStatusLoading: boolean;
    isOverviewLoading: boolean;
}

export default function HealthDashboardView(props: HealthDashboardViewProps) {
    const {
        statusOverview,
        overview,
        healthBaseUrl,
        detailSearch,
        isInitialLoading,
        isStatusLoading,
        isOverviewLoading,
    } = props;
    const { t } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();

    const alertParameters = useMemo(() => {
        const parameters = statusOverview?.summary.parameters ?? [];
        return parameters.filter(isHealthAlertParameter).sort(compareHealthAlertParameters);
    }, [statusOverview?.summary.parameters]);

    const overviewParameters = useMemo(
        () =>
            sortHealthOverviewParametersByNavigationOrder(
                (overview?.summary.parameters ?? []).map(withScoresOnSelectedDay)
            ),
        [overview?.summary.parameters]
    );

    const alertListIdentity = useMemo(
        () => alertParameters.map((parameter) => parameter.key).join(','),
        [alertParameters]
    );

    const [expandedKey, setExpandedKey] = useState<string | null>(null);

    useEffect(() => {
        if (alertParameters.length === 0) {
            setExpandedKey(null);
            return;
        }

        setExpandedKey((current) => {
            if (current != null && alertParameters.some((parameter) => parameter.key === current)) {
                return current;
            }
            return alertParameters[0]?.key ?? null;
        });
    }, [statusOverview?.cageId, alertListIdentity]);

    if (isInitialLoading) {
        return (
            <GridLayout
                contentLayout="3-1"
                className={styles.pageGrid}
                data-testid="health-dashboard">
                <HealthDashboardSkeleton />
            </GridLayout>
        );
    }

    const woundDailyTrend = overview?.charts.woundDailyTrend ?? [];

    function getParameterLabel(key: string): string {
        const uiConfig = getHealthMetricUiConfig(key);
        return uiConfig.labelKey ? tGlobal(uiConfig.labelKey) : key;
    }

    function getParameterDetailUrl(key: string): string | null {
        const uiConfig = getHealthMetricUiConfig(key);
        return uiConfig.healthPath && healthBaseUrl
            ? `${healthBaseUrl}/${uiConfig.healthPath}${detailSearch}`
            : null;
    }

    return (
        <GridLayout contentLayout="3-1" className={styles.pageGrid} data-testid="health-dashboard">
            <div className={styles.mainColumn}>
                <Card as="section" padding="lg" fullWidth className={styles.alertsSection}>
                    <div className={styles.alertsHeadingRow}>
                        <h2 className={styles.alertsHeading}>{t('dashboard.statusTitle')}</h2>
                        <InfoTip
                            topic={t('dashboard.statusTitle')}
                            content={t('dashboard.highlightTooltip')}
                            maxWidth={280}
                        />
                    </div>

                    {alertParameters.length === 0 && !isStatusLoading ? (
                        <Alert
                            tone="success"
                            live="off"
                            icon={<SvgIcon icon={CircleCheck} color="currentColor" />}>
                            {t('dashboard.allParametersNormal')}
                        </Alert>
                    ) : (
                        <div className={styles.alertsList}>
                            {alertParameters.map((parameter) => {
                                const title = getParameterLabel(parameter.key);
                                const detailUrl = getParameterDetailUrl(parameter.key);

                                return (
                                    <HealthAlertCard
                                        key={parameter.key}
                                        parameter={parameter}
                                        title={title}
                                        expanded={expandedKey === parameter.key}
                                        onToggle={() =>
                                            setExpandedKey((current) =>
                                                current === parameter.key ? null : parameter.key
                                            )
                                        }
                                        detailUrl={detailUrl}
                                        isChartLoading={isStatusLoading}
                                    />
                                );
                            })}
                        </div>
                    )}
                </Card>

                <Card as="section" padding="lg" fullWidth>
                    <LaksvelOverviewChart
                        parameters={overviewParameters}
                        woundDailyTrend={woundDailyTrend}
                        getLabel={getParameterLabel}
                        getDetailUrl={getParameterDetailUrl}
                        isLoading={isOverviewLoading && overviewParameters.length === 0}
                        isRefreshing={isOverviewLoading}
                        isExportDisabled={isOverviewLoading}
                    />
                </Card>
            </div>

            <div className={styles.infoColumn}>
                <HealthDashboardInfoPanel asOf={statusOverview?.context.asOf} />
            </div>
        </GridLayout>
    );
}
